// The ```netwatch-findings block: what the JSON means and what the component
// draws — no React, so a test runner can import it without jsdom (same split
// as chartSpec/NetwatchChart).
//
// The block is built end-to-end by the backend collector
// (app/graphs/tools/citrix_health.py, docs/runbook-findings-block.md): every
// sentence, verdict and number in it is a code template. This module only
// validates the shape tolerantly, derives presentation (verdict icon level,
// day-card kinds, sparkline paths) and refuses to draw anything it can't read.

import type { ChartBlock } from './chartSpec'
import type { SeverityKey } from './severity'

/** `critical` (docs/findings-everywhere.md) is the red Disaster octagon — a
 *  host offline now, a drive full within days, hosts below SLA. Older
 *  bundles paint it as the green tick (unknown key → clean). */
export type FindingsVerdictKey = 'clean' | 'moved' | 'no_spike' | 'chronic' | 'critical'
export type DayOutcome = 'clean' | 'spike' | 'no_data'

export interface FindingsDay {
  start: number
  end: number
  label: string
  outcome: DayOutcome
  peak: number | null
  spike_at: number | null
  cleared_at: number | null
  /** [t, y] pairs, only for spike days that still had raw history. */
  points: [number, number][] | null
  spike_at_precision?: 'hour'
  /** `17:58` / `~17:00` formatted server-side — the fallback when the block
   *  carries no epoch; the viewer's clock (from `spike_at`) wins otherwise. */
  spike_label?: string
  /** Backend-composed caption override ("high all hour" on a chronic strip) —
   *  wins over the derived "spike HH:MM". */
  caption?: string
  /** v2: spikes of the same metric OUTSIDE the asked window on this day
   *  (hour precision — grouped from hourly trends, zero extra fetches). */
  other?: FindingsOtherSpan[]
  /** A code-composed rider after the caption ("+3 hosts", "ongoing"). */
  note?: string
}

/** One off-window elevated span (pattern v2). `label` is server-composed
 *  ("~03:00"); the viewer clock derives one when absent. */
export interface FindingsOtherSpan {
  from: number
  till: number
  peak: number | null
  precision?: 'hour'
  label?: string
  /** A rider for the span's tooltip ("+1 host"). */
  note?: string
  /** This span never reached the alert line — it is only high for THIS
   *  series (app/lib/findings/pattern.py `relative_level`). Drawn muted and
   *  said out loud, because a hollow mark otherwise means "crossed the line,
   *  outside your window" and that meaning must not widen silently. */
  relative?: boolean
}

export interface FindingsPatternTally {
  in_window: number
  other: number
  clean: number
  no_data: number
}

export interface FindingsPattern {
  title: string
  subtitle: string
  summary: string
  tone: 'neutral' | 'warn'
  unit: string
  threshold: number | null
  days: FindingsDay[]
  /** 2 arms the lane renderer; absent/1 renders the legacy day strip. */
  version?: number
  tally?: FindingsPatternTally
  /** Chronic/plateau only: the hourly [epoch, value] series (heat grid, PR 3). */
  hours?: [number, number][]
  chronic?: boolean
  plateau?: boolean
  /** `derived` = the producer found the window itself (a recurring outage
   *  band) — dashed column, "recurring window" tag; `latest` = no habit, the
   *  column just marks the latest outage's hour. `span` = the lanes ARE the
   *  window (a window too long to have a "same hour" draws its own hours
   *  instead of a strip), so there is no column to draw at all. Absent = the
   *  asked hour. */
  window_kind?: 'asked' | 'derived' | 'latest' | 'span'
  /** Producer wording for the tally row (an outage card says "up all day",
   *  not "clean"); each key falls back to the metric-card default. */
  tally_labels?: { in_window?: string; other?: string; clean?: string; no_data?: string }
  /** One extra code-composed tally figure ("1h 3m offline total"). */
  tally_note?: string
  /** The word before a mark's clock in lane captions — "spike" unless the
   *  producer says otherwise ("down" on an outage card). */
  mark_word?: string
  /** IANA zone every label in this strip renders in — and the zone the prose
   *  around it was composed in. Absent on blocks built before the card had
   *  one clock; those fall back to the viewer's. See docs/timezones.md. */
  tz?: string | null
}

export interface FindingsMover {
  check: string
  label: string
  kind: 'deviating' | 'in_band'
  severity: 'warn' | 'strong' | null
  outcome: string
  correlation: number | null
  times_baseline: number | null
  detail: string
  value_text: string
  baseline_text: string | null
  chart: ChartBlock | null
}

export interface FindingsCheck {
  label: string
  outcome: string
  value_text: string
  /** Still emitted by the builder (and present on stored blocks) but no
   *  longer rendered on the metrics card — the table shows the metric and
   *  its min–max only. */
  baseline_text: string | null
  times_baseline: number | null
  correlation: number | null
  /** `min – max` across the spike windows (today's plus each earlier spike
   *  day in the strip) — the hours the pattern section is about, not today's
   *  average. Absent on blocks built before ranges existed. */
  range_text?: string | null
  /** How many of those windows had raw history for this metric. */
  windows?: number
}

export interface FindingsGroup {
  key: string
  label: string
  count: number
  /** `normal`, or `elevated` when a member sits over its threshold at the
   *  host's own usual level (kept out of the evidence by the baseline rule,
   *  but not "within range"). */
  outcome: string
  checks: FindingsCheck[]
}

/** Pill for a group / check outcome. */
export function groupPill(outcome: string): { cls: 'normal' | 'warn' | 'muted'; word: string } {
  if (outcome === 'normal') return { cls: 'normal', word: 'normal' }
  if (outcome === 'elevated') return { cls: 'warn', word: 'high, usual' }
  return { cls: 'muted', word: outcome }
}

/** One deterministic hypothesis check (the alert investigation's slate):
 *  what was tested and whether the evidence supports or refutes it. */
export interface FindingsSlateItem {
  check: string
  hypothesis: string
  outcome: 'supports' | 'refutes' | 'unknown' | string
  detail: string
  /** The outcome-RESOLVED sentence ("Not a fleet-wide issue — isolated to
   *  this host"), composed backend-side where the outcome is known. The row
   *  renders this as its primary line — a beta tester read the bare
   *  hypothesis as a finding, which the small "ruled out" pill couldn't
   *  undo. Absent on legacy rows → the hypothesis renders as before. */
  finding?: string
}

export interface FindingsSlate {
  title: string
  summary?: string
  items: FindingsSlateItem[]
}

/** One occurrence the strip shows, oldest first — the slots a row's `seen`
 *  entries index into (docs/process-recurrence.md amendment 1). `data` is
 *  false when that day's snapshot history had already been trimmed: the
 *  occurrence still holds its slot, because the reader counts occurrences on
 *  the strip, not successful reads. */
export interface FindingsOccurrence {
  from: number
  till: number
  label: string
  today: boolean
  data: boolean
}

/** Where a process was seen: which occurrence, the clock it peaked at inside
 *  it (viewer's clock wins over the server-stamped `label`, like the strip),
 *  and that peak. */
export interface FindingsProcessSeen {
  i: number
  at: number | null
  label: string
  peak_text: string
}

/** One process that recurred across the spike windows (docs/process-recurrence.md):
 *  its min–max over the samples it was listed in, and how many of the
 *  windows it appeared in. Backend-composed; `text` is the same row as a
 *  line for the `lines` fallback. */
export interface FindingsProcessRow {
  name: string
  range_text: string
  windows: number
  /** Windows that had snapshot history — the measured count, NOT the
   *  denominator (a trimmed day must not shrink the fraction). */
  windows_total: number
  /** Every occurrence on the strip: the denominator the reader can count. */
  occurrences_total?: number
  /** Which occurrences it was listed in, and its peak inside each. */
  seen?: FindingsProcessSeen[]
  /** Share of samples it was listed in — the single-window basis. */
  presence?: number
  unit?: string
  text?: string
}

/** One process named inside a single out-of-window span, with its peak there
 *  and the clock it peaked at (viewer's clock wins over the server label). */
export interface FindingsProcessDriverHit {
  name: string
  peak_text: string
  at?: number | null
  label?: string
}

/** One elevated stretch OUTSIDE every strip window (docs/process-recurrence.md
 *  amendment 2). `relative` spans never reached the alert line — they were high
 *  for this host — and must never be labelled spikes. */
export interface FindingsProcessSpan {
  label: string
  /** The day alone ("Sun 6"), server-composed; the hour comes from `from`. */
  day?: string
  from: number
  till: number
  relative?: boolean
  /** The lead metric's peak there, as the strip drew it. */
  peak_text?: string | null
  /** The strip's own wording for a relative span ("5x this host's usual"). */
  note?: string | null
  names: FindingsProcessDriverHit[]
  samples?: number
  /** Why the span names nothing: history we do not have, or a machine that
   *  was quiet under the floor. Opposite facts, never one sentence. */
  empty_text?: string
  text?: string
}

/** One process across ALL the out-of-window hours — a ledger row in the grid's
 *  own shape (`FindingsProcessRow`) plus the two facts that are the ledger's
 *  own: the client-readable `plain` name from the backend's lexicon (absent
 *  when the process is unknown; `name` is always what the agent measured), and
 *  whether it also ran inside the window. */
export interface FindingsProcessLedgerRow extends FindingsProcessRow {
  plain?: string | null
  /** Indices into `FindingsProcessOther.occurrences` — which hours it ran in. */
  spans?: number[]
  /** Also listed in the grid above. */
  in_window?: boolean
}

/** The ledger under the process grid: what recurred outside the window, how
 *  much of the machine it took, and whether any of it also ran inside. */
export interface FindingsProcessOther {
  /** The finding, composed by the backend ("A backup job ran in all four of
   *  these hours"). Never authored by the model. */
  title: string
  subtitle: string
  /** One row per process, drawn by the same renderer as the grid above. */
  rows: FindingsProcessLedgerRow[]
  /** The hours themselves, as a roster the rows' `seen in` cells resolve
   *  against — and the card names them from these epochs, in the card's one
   *  clock, so the finding can never say "these four hours" without saying
   *  which (docs/timezones.md, beta feedback 2026-09-10). */
  occurrences: FindingsOccurrence[]
  spans: FindingsProcessSpan[]
  occasions: number
  /** "the four hours" — this card spells its numbers, the control included. */
  occasionsWord: string
  summary: string | null
  relativeOnly: boolean
}

/** The processes section, normalised: rows when the producer measured them,
 *  else the collector's snapshot lines (older blocks, alert strategies that
 *  never measured). */
export interface FindingsProcesses {
  title: string
  lines: string[]
  rows: FindingsProcessRow[]
  /** The snapshot nearest the peak. `at` is the epoch (viewer's clock wins
   *  over the server-stamped `label`, like the strip). */
  peak: { at: number | null; label: string; text: string } | null
  note: string | null
  basis: string | null
  windowsTotal: number
  /** Published only on the multi-window basis; empty means the cell stays a
   *  plain count (one measured window is a presence share over samples, not
   *  a per-occurrence fact). */
  occurrences: FindingsOccurrence[]
  /** Out-of-window spikes and their drivers; null when the strip found none
   *  (or the block predates amendment 2). */
  other: FindingsProcessOther | null
}

/** The diagnosis' own confidence, distinct from the host-shape verdict. */
export type FindingsVerdictState = 'confirmed' | 'probable' | 'inconclusive'

/** One titled grid inside the card (docs/findings-everywhere.md): the
 *  measured rows of a producer without a lead metric — outages, hosts below
 *  SLA, drives filling, lockouts by caller. Cells are strings the backend
 *  already formatted (units, timezone); the component never formats. */
export interface FindingsTableColumn {
  key: string
  label: string
  align?: 'left' | 'right'
}
export type FindingsRowTone = 'warn' | 'strong' | 'muted'
/** Shaped tables (docs/findings-everywhere.md, "Shaped tables"): a hint
 *  beside the grid. `bars` = one bar per row against a target; `tiles` =
 *  two-line rows with inline meters and a status chip; `score` = a readout
 *  on a banded track plus one segmented bar. Absent or unreadable → grid. */
export type FindingsTableShape = 'bars' | 'tiles' | 'score'
export type FindingsChipTone = 'warn' | 'strong' | 'muted' | 'ok'
export interface FindingsChip { text: string; tone?: FindingsChipTone }
/** One drive on the time-to-full axis; `days: null` = stable, parked at the end. */
export interface FindingsRunwayDot { label: string; days: number | null; tone?: FindingsRowTone }
export interface FindingsScore { value: number; max: number; bands: number[]; grade?: string }
export interface FindingsTableRow {
  /** Colours the leading cell (warn amber, strong red) or greys the row (muted). */
  tone?: FindingsRowTone
  /** A sub-row under the previous one (lockout sources under an account). */
  indent?: boolean
  /** Meter / bar fill per column key, 0..1 — the producer's fraction, never
   *  parsed from the cell. */
  fills?: Record<string, number>
  /** The tile's status chip ("High problem", "6.1× normal"). */
  chip?: FindingsChip
  /** The second line under the lead cell ("Prod · 31 VMs"). */
  sub?: string
  [cell: string]: string | boolean | undefined | Record<string, number> | FindingsChip
}
export interface FindingsTable {
  title: string
  columns: FindingsTableColumn[]
  rows: FindingsTableRow[]
  /** Code-composed line under the grid ("2 more not shown"). */
  note?: string | null
  /** Shown instead of the grid when there are no rows. */
  empty?: string | null
  shape?: FindingsTableShape | null
  /** The strain tick on every meter / bar, as a fraction of it. */
  floor?: number | null
  /** tiles: drives placed on a now · 1 week · 1 month · 3 months · stable axis. */
  runway?: FindingsRunwayDot[] | null
  /** score shape: the readout and the grade bands it is judged against. */
  score?: FindingsScore | null
}

/** The JSON inside a ```netwatch-findings fence, once hydrated. */
export interface FindingsBlock {
  kind?: string
  version?: number
  ref?: string
  /** IANA zone every epoch in this block is to be read in — the card's one
   *  clock. `pattern.tz` used to be the only carrier, so a window too long
   *  for a strip left the card with no zone at all and every client-formatted
   *  label fell back to the viewer's laptop. Absent on older blocks; those
   *  still fall back to `pattern.tz`, then the viewer's. See docs/timezones.md. */
  tz?: string | null
  /** `caption` is one backend-composed line under the verdict ("CPU
   *  utilisation on app-01, 1h to 14:57"); the component never composes prose. */
  subject?: { type?: string; name?: string; host?: string; caption?: string }
  window?: { from: number; till: number; minutes: number }
  verdict?: { key: FindingsVerdictKey | string; title: string; detail: string; state?: FindingsVerdictState | string | null }
  checked?: number
  lead?: { check: string; label: string; unit: string; spiked: boolean; detail?: string } | null
  pattern?: FindingsPattern | null
  /** null = this producer never measured movers (a checks-only alert
   *  strategy) — the section is omitted, not titled "Nothing moved with it". */
  movers?: {
    title: string
    summary: string
    strongest_correlation: number | null
    link_threshold: number
    items: FindingsMover[]
  } | null
  normal?: {
    title: string
    summary: string
    groups: FindingsGroup[]
    /** The spike windows the groups were measured over, when there was more
     *  than today's (the title then says "measured during the spikes"). */
    windows?: Array<{ from: number; till: number; label: string }>
    gaps: { count: number; labels: string[] }
  } | null
  slate?: FindingsSlate | null
  look?: { title: string; text: string } | null
  /** `title` arms the in-card render (alert producer, amendment 20) — an
   *  untitled `next` (runbook/spike vocabulary asks) stays machine-facing. */
  next?: { title?: string; ask_now: string[]; needs_collection: string[] }
  /** Amendment 20 (alert producer): the collector's kind-tagged evidence
   *  sections and the host's other open problems, lifted into the card so
   *  the whole investigation renders as one card. Lines are markdown-ish
   *  bullets ("- …", nested "  - …", `**bold**`, backtick code). */
  recurring?: { title: string; subtitle?: string; lines: string[] } | null
  /** `rows`/`peak` arrive from producers that measured processes across the
   *  spike windows; `lines` alone is the older shape (still rendered). */
  processes?: {
    title: string
    lines?: string[]
    rows?: FindingsProcessRow[]
    peak?: { at?: number | null; label?: string; text?: string } | null
    note?: string | null
    basis?: string | null
    windows_total?: number
    occurrences?: FindingsOccurrence[]
    other?: {
      title?: string
      subtitle?: string
      rows?: FindingsProcessLedgerRow[]
      occurrences?: FindingsOccurrence[]
      spans?: FindingsProcessSpan[]
      occasions?: number
      occasions_word?: string
      relative_only?: boolean
      summary?: string | null
      lines?: string[]
    } | null
  } | null
  problems?: { title: string; count: number; names: string[] } | null
  /** Titled grids from producers without a lead (docs/findings-everywhere.md).
   *  Absent on strip producers; `[]` is legal (a pill-only minimal card). */
  tables?: FindingsTable[] | null
  [key: string]: unknown
}

/** Shape-sniff only — a findings block is anything with a verdict or a ref. */
export function parseFindingsBlock(raw: string): FindingsBlock | null {
  try {
    const data = JSON.parse(raw)
    if (
      data && typeof data === 'object' &&
      ((data.verdict && typeof data.verdict === 'object') || typeof data.ref === 'string')
    ) return data as FindingsBlock
  } catch {
    // Incomplete JSON while streaming, or malformed — the component falls back.
  }
  return null
}

export interface FindingsView {
  verdict: { key: FindingsVerdictKey; title: string; detail: string; level: SeverityKey; state: FindingsVerdictState | null }
  checked: number
  host: string
  caption: string | null
  /** The lead metric family read as one signal (peak, worst session, average). */
  leadDetail: string | null
  pattern: FindingsPattern | null
  /** The one clock every label on this card is drawn in, or null for the
   *  viewer's. Resolved once from the block, then the strip. */
  tz: string | null
  /** null when the producer never measured movers — section omitted. */
  movers: NonNullable<FindingsBlock['movers']> | null
  normal: NonNullable<FindingsBlock['normal']> | null
  slate: FindingsSlate | null
  look: { title: string; text: string } | null
  recurring: { title: string; subtitle?: string; lines: string[] } | null
  processes: FindingsProcesses | null
  problems: { title: string; count: number; names: string[] } | null
  /** Non-null only when the producer titled it (the alert card's "What
   *  would settle it" / "Worth checking next"). */
  next: { title: string; items: string[] } | null
  /** Readable grids, in producer order; malformed entries dropped, never the card. */
  tables: FindingsTable[]
}

export type FindingsResult =
  | { ok: true; view: FindingsView }
  | { ok: false; reason: 'findings not loaded' | 'malformed findings' }

const VERDICT_KEYS: FindingsVerdictKey[] = ['clean', 'moved', 'no_spike', 'chronic', 'critical']
const VERDICT_STATES: FindingsVerdictState[] = ['confirmed', 'probable', 'inconclusive']
const ROW_TONES: FindingsRowTone[] = ['warn', 'strong', 'muted']
const TABLE_SHAPES: FindingsTableShape[] = ['bars', 'tiles', 'score']
const CHIP_TONES: FindingsChipTone[] = ['warn', 'strong', 'muted', 'ok']

function fraction(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 1 ? v : null
}

/** Which shaped icon heads the verdict line. Clean/no-spike = the green
 *  Normal check; movers = the runbook's own severity (strong → High);
 *  chronic (saturated around the clock) is never a green tick; critical is
 *  the red Disaster octagon. */
export function verdictLevel(key: FindingsVerdictKey, movers: FindingsMover[]): SeverityKey {
  if (key === 'critical') return 5
  if (key === 'chronic') return 2
  if (key !== 'moved') return 'ok'
  return movers.some((m) => m.kind === 'deviating' && m.severity === 'strong') ? 4 : 2
}

export function normalizeFindings(data: FindingsBlock | null | undefined): FindingsResult {
  if (!data || typeof data !== 'object') return { ok: false, reason: 'malformed findings' }
  const verdict = data.verdict
  if (!verdict || typeof verdict !== 'object') {
    // A bare ref: the backend expands it before the answer is stored, so this
    // is a block ahead of its payload (streaming) or an expired one.
    return { ok: false, reason: typeof data.ref === 'string' ? 'findings not loaded' : 'malformed findings' }
  }
  const key = VERDICT_KEYS.includes(verdict.key as FindingsVerdictKey)
    ? (verdict.key as FindingsVerdictKey)
    : 'clean'
  // A producer that measured nothing (movers: null / normal: null) gets no
  // section; a malformed one (present but unreadable) gets an empty section,
  // so a broken payload still shows its verdict.
  const movers = data.movers === null
    ? null
    : data.movers && Array.isArray(data.movers.items)
      ? data.movers
      : { title: '', summary: '', strongest_correlation: null, link_threshold: 0.6, items: [] }
  const normal = data.normal === null
    ? null
    : data.normal && Array.isArray(data.normal.groups)
      ? data.normal
      : { title: '', summary: '', groups: [], gaps: { count: 0, labels: [] } }
  const rawPattern = data.pattern && Array.isArray(data.pattern.days) && data.pattern.days.length
    ? data.pattern
    : null
  // One clock per card: the block states it; a block built before it did
  // (or by a producer that only stamped the strip) falls back to the strip's.
  const tz = (typeof data.tz === 'string' && data.tz)
    || (typeof rawPattern?.tz === 'string' && rawPattern.tz)
    || null
  // …and the strip renders in that same clock, so a strip whose own `tz` is
  // missing can never drift away from the prose beside it.
  const pattern = rawPattern ? { ...rawPattern, tz: rawPattern.tz ?? tz } : null
  const slate = data.slate && Array.isArray(data.slate.items) && data.slate.items.length
    ? data.slate
    : null
  const state = VERDICT_STATES.includes(verdict.state as FindingsVerdictState)
    ? (verdict.state as FindingsVerdictState)
    : null
  return {
    ok: true,
    view: {
      verdict: {
        key,
        title: String(verdict.title ?? ''),
        detail: String(verdict.detail ?? ''),
        level: verdictLevel(key, movers?.items ?? []),
        state,
      },
      tz,
      checked: typeof data.checked === 'number' ? data.checked : 0,
      host: String(data.subject?.host ?? ''),
      caption: typeof data.subject?.caption === 'string' && data.subject.caption ? data.subject.caption : null,
      leadDetail: typeof data.lead?.detail === 'string' && data.lead.detail ? data.lead.detail : null,
      pattern,
      movers,
      normal,
      slate,
      look: data.look && typeof data.look.text === 'string' ? data.look : null,
      recurring: titledLines(data.recurring),
      processes: processSection(data.processes),
      problems:
        data.problems && typeof data.problems.title === 'string' && Array.isArray(data.problems.names)
          ? { title: data.problems.title,
              count: typeof data.problems.count === 'number' ? data.problems.count : data.problems.names.length,
              names: data.problems.names.filter((n): n is string => typeof n === 'string' && !!n.trim()) }
          : null,
      next:
        data.next && typeof data.next.title === 'string' && data.next.title &&
        Array.isArray(data.next.ask_now) && data.next.ask_now.length
          ? { title: data.next.title,
              items: data.next.ask_now.filter((s): s is string => typeof s === 'string' && !!s.trim()) }
          : null,
      tables: tableSections(data.tables),
    },
  }
}

/** The readable grids of `tables`, in order. An entry needs a title, at
 *  least one column with a key, and a rows array; rows keep only string
 *  cells plus a known tone / indent, so a stray number or object never
 *  reaches the DOM. Malformed entries are dropped, never the card. */
function tableSections(raw: unknown): FindingsTable[] {
  if (!Array.isArray(raw)) return []
  const out: FindingsTable[] = []
  for (const t of raw) {
    if (!t || typeof t !== 'object') continue
    const tbl = t as Partial<FindingsTable>
    if (typeof tbl.title !== 'string' || !Array.isArray(tbl.columns) || !Array.isArray(tbl.rows)) continue
    const columns: FindingsTableColumn[] = []
    for (const c of tbl.columns) {
      if (!c || typeof c !== 'object' || typeof c.key !== 'string' || !c.key) continue
      columns.push({ key: c.key, label: typeof c.label === 'string' ? c.label : c.key,
                     align: c.align === 'right' ? 'right' : 'left' })
    }
    if (!columns.length) continue
    const rows: FindingsTableRow[] = []
    for (const r of tbl.rows) {
      if (!r || typeof r !== 'object') continue
      const row: FindingsTableRow = {}
      for (const c of columns) {
        const v = (r as Record<string, unknown>)[c.key]
        row[c.key] = typeof v === 'string' ? v : ''
      }
      const tone = (r as Record<string, unknown>).tone
      if (ROW_TONES.includes(tone as FindingsRowTone)) row.tone = tone as FindingsRowTone
      if ((r as Record<string, unknown>).indent === true) row.indent = true
      // Shape hints on the row: fills only for columns that exist and read
      // as fractions; a chip needs its text; a sub needs words.
      const fillsRaw = (r as Record<string, unknown>).fills
      if (fillsRaw && typeof fillsRaw === 'object') {
        const fills: Record<string, number> = {}
        for (const c of columns) {
          const f = fraction((fillsRaw as Record<string, unknown>)[c.key])
          if (f !== null) fills[c.key] = f
        }
        if (Object.keys(fills).length) row.fills = fills
      }
      const chipRaw = (r as Record<string, unknown>).chip as Partial<FindingsChip> | undefined
      if (chipRaw && typeof chipRaw === 'object' && typeof chipRaw.text === 'string' && chipRaw.text.trim()) {
        row.chip = CHIP_TONES.includes(chipRaw.tone as FindingsChipTone)
          ? { text: chipRaw.text, tone: chipRaw.tone as FindingsChipTone }
          : { text: chipRaw.text }
      }
      const sub = (r as Record<string, unknown>).sub
      if (typeof sub === 'string' && sub.trim()) row.sub = sub
      rows.push(row)
    }
    const parsed: FindingsTable = {
      title: tbl.title, columns, rows,
      note: typeof tbl.note === 'string' && tbl.note ? tbl.note : null,
      empty: typeof tbl.empty === 'string' && tbl.empty ? tbl.empty : null,
      shape: TABLE_SHAPES.includes(tbl.shape as FindingsTableShape) ? (tbl.shape as FindingsTableShape) : null,
      floor: fraction(tbl.floor),
      runway: runwayDots(tbl.runway),
      score: scoreOf(tbl.score),
    }
    out.push(parsed)
  }
  return out
}

function runwayDots(raw: unknown): FindingsRunwayDot[] | null {
  if (!Array.isArray(raw)) return null
  const out: FindingsRunwayDot[] = []
  for (const d of raw) {
    if (!d || typeof d !== 'object') continue
    const o = d as Record<string, unknown>
    if (typeof o.label !== 'string' || !o.label.trim()) continue
    const days = typeof o.days === 'number' && Number.isFinite(o.days) && o.days >= 0 ? o.days : null
    const dot: FindingsRunwayDot = { label: o.label, days }
    if (ROW_TONES.includes(o.tone as FindingsRowTone)) dot.tone = o.tone as FindingsRowTone
    out.push(dot)
  }
  return out.length ? out : null
}

function scoreOf(raw: unknown): FindingsScore | null {
  if (!raw || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  const max = typeof o.max === 'number' && Number.isFinite(o.max) && o.max > 0 ? o.max : 100
  if (typeof o.value !== 'number' || !Number.isFinite(o.value) || o.value < 0 || o.value > max) return null
  const bands = Array.isArray(o.bands)
    ? o.bands.filter((b): b is number => typeof b === 'number' && Number.isFinite(b) && b >= 0 && b <= max).sort((a, b) => a - b)
    : []
  const score: FindingsScore = { value: o.value, max, bands }
  if (typeof o.grade === 'string' && o.grade.trim()) score.grade = o.grade
  return score
}

/** A `{title, lines}` card section, or null when absent/empty/malformed. */
function titledLines(
  s: { title?: unknown; subtitle?: unknown; lines?: unknown } | null | undefined,
): { title: string; subtitle?: string; lines: string[] } | null {
  if (!s || typeof s.title !== 'string' || !s.title || !Array.isArray(s.lines)) return null
  const lines = s.lines.filter((l): l is string => typeof l === 'string' && !!l.trim())
  if (!lines.length) return null
  const subtitle = typeof s.subtitle === 'string' && s.subtitle.trim() ? s.subtitle : undefined
  return { title: s.title, subtitle, lines }
}

function isProcessRow(r: unknown): r is FindingsProcessRow {
  if (!r || typeof r !== 'object') return false
  const o = r as Record<string, unknown>
  return typeof o.name === 'string' && !!o.name && typeof o.range_text === 'string' &&
    typeof o.windows === 'number' && typeof o.windows_total === 'number'
}

function isOccurrence(o: unknown): o is FindingsOccurrence {
  if (!o || typeof o !== 'object') return false
  const v = o as Record<string, unknown>
  return typeof v.from === 'number' && Number.isFinite(v.from) && typeof v.till === 'number'
}

/** A row's occurrence hits, cleaned: an entry needs a slot and a clock or a
 *  server label, or it can say nothing about when. */
function processSeen(row: FindingsProcessRow): FindingsProcessSeen[] {
  if (!Array.isArray(row.seen)) return []
  return row.seen.filter((s): s is FindingsProcessSeen => {
    if (!s || typeof s !== 'object') return false
    const v = s as unknown as Record<string, unknown>
    return typeof v.i === 'number' && v.i >= 0 &&
      (typeof v.at === 'number' || (typeof v.label === 'string' && !!v.label))
  })
}

/** The processes section: rows + peak when measured, lines otherwise, null
 *  when there is nothing readable to draw. */
function processSection(s: FindingsBlock['processes']): FindingsProcesses | null {
  if (!s || typeof s !== 'object' || typeof s.title !== 'string' || !s.title) return null
  const rows = Array.isArray(s.rows) ? s.rows.filter(isProcessRow) : []
  const occurrences = Array.isArray(s.occurrences) ? s.occurrences.filter(isOccurrence) : []
  const lines = Array.isArray(s.lines)
    ? s.lines.filter((l): l is string => typeof l === 'string' && !!l.trim())
    : []
  const peak = s.peak && typeof s.peak === 'object' && typeof s.peak.text === 'string' && s.peak.text
    ? { at: typeof s.peak.at === 'number' ? s.peak.at : null,
        label: typeof s.peak.label === 'string' ? s.peak.label : '',
        text: s.peak.text }
    : null
  const other = processOther(s.other)
  if (!rows.length && !lines.length && !peak && !other) return null
  return {
    title: s.title,
    lines,
    rows,
    peak,
    note: typeof s.note === 'string' && s.note ? s.note : null,
    basis: typeof s.basis === 'string' ? s.basis : null,
    windowsTotal: typeof s.windows_total === 'number' ? s.windows_total : 0,
    occurrences,
    other,
  }
}

/** The out-of-window ledger, or null when the block carries none (every block
 *  written before amendment 2). A span with no readable driver still renders —
 *  the strip above drew that hour — but a span without a label or a clock says
 *  nothing about when, and goes. */
function processOther(o: unknown): FindingsProcessOther | null {
  if (!o || typeof o !== 'object') return null
  const v = o as Record<string, unknown>
  const spans = Array.isArray(v.spans) ? (v.spans as unknown[]).filter(isProcessSpan) : []
  const rows = Array.isArray(v.rows) ? (v.rows as unknown[]).filter(isProcessRow) : []
  if (!spans.length || typeof v.title !== 'string' || !v.title) return null
  const occurrences = Array.isArray(v.occurrences)
    ? (v.occurrences as unknown[]).filter(isOccurrence)
    // A block written before amendment 4 carries no roster; the hours are the
    // spans, and they are what the rows count against.
    : spans.map((sp) => ({ from: sp.from, till: sp.till, label: sp.label, today: false, data: true }))
  return {
    title: v.title,
    subtitle: typeof v.subtitle === 'string' ? v.subtitle : '',
    rows: (rows as FindingsProcessLedgerRow[]).map((d) => ({
      ...d,
      spans: Array.isArray(d.spans) ? d.spans.filter((i) => typeof i === 'number' && i >= 0) : [],
    })),
    occurrences,
    spans: spans.map((sp) => ({
      ...sp,
      names: Array.isArray(sp.names) ? sp.names.filter(isDriverHit) : [],
    })),
    occasions: typeof v.occasions === 'number' ? v.occasions : spans.length,
    occasionsWord: typeof v.occasions_word === 'string' && v.occasions_word
      ? v.occasions_word
      : `${typeof v.occasions === 'number' ? v.occasions : spans.length} hours`,
    summary: typeof v.summary === 'string' && v.summary ? v.summary : null,
    relativeOnly: v.relative_only === true,
  }
}

function isProcessSpan(sp: unknown): sp is FindingsProcessSpan {
  if (!sp || typeof sp !== 'object') return false
  const v = sp as Record<string, unknown>
  return typeof v.label === 'string' && !!v.label &&
    typeof v.from === 'number' && Number.isFinite(v.from)
}

function isDriverHit(n: unknown): n is FindingsProcessDriverHit {
  if (!n || typeof n !== 'object') return false
  const v = n as Record<string, unknown>
  return typeof v.name === 'string' && !!v.name && typeof v.peak_text === 'string' && !!v.peak_text
}

/** One slot of the `seen in` cell. */
export type OccurrenceState = 'seen' | 'unseen' | 'no_data'
export interface OccurrenceSlot {
  state: OccurrenceState
  /** The occurrence's day, in the viewer's clock where the epoch allows it. */
  label: string
  today: boolean
  /** `seen` slots only: when it peaked inside that occurrence, and how high. */
  clock: string
  peak: string
}

/** The `seen in` cell resolved against the roster: one slot per occurrence,
 *  in strip order. Empty when there is no roster to resolve against (single
 *  measured window, or a block built before the roster existed) — the cell
 *  stays the plain count it has always been. */
export function occurrenceSlots(
  row: FindingsProcessRow, occurrences: FindingsOccurrence[], tz?: string | null,
): OccurrenceSlot[] {
  if (occurrences.length < 2) return []
  const hits = new Map(processSeen(row).map((s) => [s.i, s]))
  if (!hits.size) return []
  return occurrences.map((o, i) => {
    const hit = hits.get(i)
    return {
      state: hit ? 'seen' : o.data ? 'unseen' : 'no_data',
      label: dayLabel({ start: o.from, label: o.label }, tz),
      today: !!o.today,
      clock: hit ? (typeof hit.at === 'number' ? clockLabel(hit.at, tz) : hit.label || '') : '',
      peak: hit?.peak_text || '',
    } satisfies OccurrenceSlot
  })
}

/** How many occurrences it was seen in, over how many there were. The
 *  denominator is every occurrence on the strip — a day whose snapshot
 *  history was trimmed keeps its slot, so the fraction always matches what
 *  the reader counts above (beta feedback: "we have four occurrences, I was
 *  expecting 2/4"). */
export function seenFraction(row: FindingsProcessRow, occurrences: FindingsOccurrence[]): { n: number; total: number } {
  return { n: row.windows, total: row.occurrences_total || occurrences.length || row.windows_total }
}

/** Pill class for a slate outcome — refuted hypotheses read muted (ruled
 *  out), supported ones warn, undecided ones grey. */
export function slatePill(outcome: string): 'normal' | 'warn' | 'muted' {
  if (outcome === 'refutes') return 'normal'
  if (outcome === 'supports') return 'warn'
  return 'muted'
}

export function slateWord(outcome: string): string {
  if (outcome === 'refutes') return 'ruled out'
  if (outcome === 'supports') return 'supports'
  return 'undecided'
}

// --- day strip -----------------------------------------------------------------

/** What a day card shows: the green tick for a clean day (the whole card),
 *  a sparkline for a spike day that still has raw history, a peak-only note
 *  for a spike day where only trends survived, a dash for no data. */
export type DayKind = 'tick' | 'spark' | 'peak' | 'none'

export function dayKind(day: FindingsDay): DayKind {
  if (day.outcome === 'clean') return 'tick'
  if (day.outcome === 'spike') return day.points && day.points.length >= 2 ? 'spark' : 'peak'
  return 'none'
}

/** SVG path for a day sparkline in a `width`×`height` box; y from 0 to the
 *  larger of the day's own peak and the threshold, so a spike is tall on every
 *  card and a card never rescales the same threshold differently. */
export function sparklinePath(
  points: [number, number][],
  width: number,
  height: number,
  ceiling: number | null,
): string {
  if (points.length < 2) return ''
  const xs = points.map((p) => p[0])
  const ys = points.map((p) => p[1])
  const x0 = Math.min(...xs)
  const x1 = Math.max(...xs)
  const top = Math.max(ceiling ?? 0, ...ys, 1e-9)
  const pad = 1.5
  const w = width - pad * 2
  const h = height - pad * 2
  return points
    .map(([x, y], i) => {
      const px = pad + (x1 === x0 ? w / 2 : ((x - x0) / (x1 - x0)) * w)
      const py = pad + h - Math.max(0, Math.min(1, y / top)) * h
      return `${i === 0 ? 'M' : 'L'}${px.toFixed(1)} ${py.toFixed(1)}`
    })
    .join(' ')
}

/** Threshold guide line y in the same box as `sparklinePath`. */
export function sparklineThresholdY(points: [number, number][], height: number, ceiling: number | null): number | null {
  if (ceiling == null || points.length < 2) return null
  const top = Math.max(ceiling, ...points.map((p) => p[1]), 1e-9)
  const pad = 1.5
  const h = height - pad * 2
  return pad + h - Math.max(0, Math.min(1, ceiling / top)) * h
}

// ONE CLOCK PER CARD. Every label here is drawn in `pattern.tz` - the IANA
// zone the server resolved for this answer (the user's Zabbix profile zone,
// falling back to the monitored instance's). It is the same zone the prose
// above the strip is composed in, so the header, the lanes, the captions and
// the chart axis underneath all read the same.
//
// This used to be the viewer's browser clock, and that was itself a fix: the
// server stamped `label` / `spike_label` in its display zone, which on the
// first real runs resolved to UTC while the charts under it read local time -
// "spike 12:11" over an axis running 14:57-15:57. Moving the strip to the
// browser clock aligned it with the chart and broke it against the prose
// instead: one live card showed "Window: 03:11 EDT" in its header and
// "10:11-11:11" in its strip, seven hours apart, same instant. Whichever pair
// you align locally, the other drifts - so the zone is now carried in the
// payload and every renderer takes it. See docs/timezones.md.
//
// `tz` absent or unusable (an older block, a runtime that rejects the name)
// falls back to the viewer's clock, which is what this did before.

const _ZONE_FORMATS = new Map<string, Intl.DateTimeFormat>()

function zoneFormat(tz?: string | null): Intl.DateTimeFormat {
  const key = tz || ''
  const cached = _ZONE_FORMATS.get(key)
  if (cached) return cached
  // `hourCycle: 'h23'` keeps midnight at 00, not 24; `en-US` fixes the part
  // vocabulary ("Wed") independently of the viewer's locale, exactly as the
  // hand-rolled DAY_NAMES table did.
  const opts: Intl.DateTimeFormatOptions = {
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23', weekday: 'short', day: 'numeric',
  }
  let fmt: Intl.DateTimeFormat
  try {
    fmt = new Intl.DateTimeFormat('en-US', tz ? { ...opts, timeZone: tz } : opts)
  } catch {
    fmt = new Intl.DateTimeFormat('en-US', opts)
  }
  _ZONE_FORMATS.set(key, fmt)
  return fmt
}

function zoneParts(epoch: number, tz?: string | null) {
  const parts = zoneFormat(tz).formatToParts(new Date(epoch * 1000))
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? ''
  return { hour: get('hour'), minute: get('minute'), weekday: get('weekday'), day: get('day') }
}

/** `17:58` in the card's clock. */
export function clockLabel(epoch: number, tz?: string | null): string {
  const p = zoneParts(epoch, tz)
  return `${p.hour}:${p.minute}`
}

/** The card's clock, abbreviated (`EDT`) - empty when the runtime will not
 *  name it. A label that sits beside server prose naming its own zone has to
 *  name one too, or the two read as different instants (docs/timezones.md). */
export function zoneAbbr(epoch: number, tz?: string | null): string {
  try {
    const opts: Intl.DateTimeFormatOptions = { timeZoneName: 'short', hour: 'numeric' }
    const fmt = new Intl.DateTimeFormat('en-US', tz ? { ...opts, timeZone: tz } : opts)
    return fmt.formatToParts(new Date(epoch * 1000)).find((p) => p.type === 'timeZoneName')?.value ?? ''
  } catch {
    return ''
  }
}

/** `Mon 7 01:00, Tue 8 01:00, Wed 9 01:00 and Thu 10 00:00 EDT` - WHICH hours
 *  a ledger is about, drawn from the same epochs as the strip above it. The
 *  backend cannot compose this: the card has one clock and it is the client
 *  that renders it, so a server-stamped list is a second clock waiting to
 *  disagree. Empty when there is nothing datable to name. */
export function occurrenceHours(occurrences: FindingsOccurrence[], tz?: string | null): string {
  const labels = occurrences
    .filter((o) => Number.isFinite(o.from))
    .map((o) => `${dayLabel({ start: o.from, label: o.label }, tz)} ${clockLabel(o.from, tz)}`.trim())
  if (!labels.length) return ''
  const zone = zoneAbbr(occurrences[0].from, tz)
  const list = labels.length === 1
    ? labels[0]
    : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`
  return zone ? `${list} ${zone}` : list
}

/** `Fri 8` in the card's clock - a day-strip card label. */
export function dayLabel(day: Pick<FindingsDay, 'start' | 'label'>, tz?: string | null): string {
  if (!Number.isFinite(day.start)) return day.label ?? ''
  const p = zoneParts(day.start, tz)
  return `${p.weekday} ${p.day}`
}

/** The small caption under a day card. `word` is the producer's mark word
 *  ("down" on an outage card); "spike" otherwise. */
export function dayCaption(day: FindingsDay, word = 'spike', tz?: string | null): string {
  if (day.caption) return day.caption
  if (day.outcome === 'clean') return 'clean'
  if (day.outcome === 'no_data') return 'no data'
  if (day.spike_at != null && Number.isFinite(day.spike_at)) {
    return day.spike_at_precision === 'hour'
      ? `${word} ~${clockLabel(day.spike_at, tz)}`
      : `${word} ${clockLabel(day.spike_at, tz)}`
  }
  if (day.spike_label) return `${word} ${day.spike_label}`
  return word
}

// --- week lanes (pattern v2) ---------------------------------------------------
// One 24h lane per day in the VIEWER's clock (same rule as the strip: the
// server's labels may name another zone; epochs win). All geometry is pure
// percent math so the runner covers it without jsdom.

/** Which pattern visual a block earns: `lanes` needs a v2 producer; a v2
 *  chronic / plateau strip carries `hours` and draws the `heat` grid instead
 *  (the window is not special — every hour's peak is). Old blocks and
 *  anything with unreadable epochs keep the legacy strip. */
export function patternKind(pattern: FindingsPattern): 'lanes' | 'heat' | 'strip' {
  if ((pattern.version ?? 1) < 2) return 'strip'
  if (!pattern.days.every((d) => Number.isFinite(d.start) && Number.isFinite(d.end) && d.end > d.start)) return 'strip'
  if (Array.isArray(pattern.hours) && pattern.hours.filter(isHourPair).length >= 24) return 'heat'
  return 'lanes'
}

function isHourPair(h: unknown): h is [number, number] {
  return Array.isArray(h) && h.length >= 2 && Number.isFinite(h[0]) && Number.isFinite(h[1])
}

export interface LaneBox { dayStart: number; daySeconds: number }

/** The calendar day containing the day's window, IN THE CARD'S CLOCK — the
 *  lane's x-axis, and the bucket every heat cell is indexed into.
 *
 *  This read the viewer's midnight until 2026-09-10, which made the strip's
 *  geometry the one surface `tz` had never reached: the row labels and
 *  captions rendered in the card's zone while the lanes underneath were
 *  bucketed by the browser's. A Nairobi viewer of a New York estate got every
 *  lane shifted seven hours — a 01:00 EDT backup drawn at 08:00, on the wrong
 *  day's row, under a label naming the right one. See docs/timezones.md.
 *
 *  DST days are 23/25 h; the width comes from the real next midnight. */
export function laneBox(day: Pick<FindingsDay, 'start'>, tz?: string | null): LaneBox {
  const dayStartWall = Math.floor(wallClock(day.start, tz) / 86400) * 86400
  const dayStart = epochOfWall(dayStartWall, tz)
  return { dayStart, daySeconds: Math.max(1, epochOfWall(dayStartWall + 86400, tz) - dayStart) }
}

const _WALL_FORMATS = new Map<string, Intl.DateTimeFormat>()

function wallFormat(tz?: string | null): Intl.DateTimeFormat {
  const key = tz || ''
  const cached = _WALL_FORMATS.get(key)
  if (cached) return cached
  const opts: Intl.DateTimeFormatOptions = {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
  }
  let fmt: Intl.DateTimeFormat
  try {
    fmt = new Intl.DateTimeFormat('en-US', tz ? { ...opts, timeZone: tz } : opts)
  } catch {
    fmt = new Intl.DateTimeFormat('en-US', opts)
  }
  _WALL_FORMATS.set(key, fmt)
  return fmt
}

/** The wall-clock reading of an epoch in `tz`, expressed as the epoch that
 *  same y/m/d h:m:s would be in UTC. `wallClock(e) - e` is the zone's offset
 *  at that instant, which is the only thing this is used to recover. */
function wallClock(epoch: number, tz?: string | null): number {
  const parts = wallFormat(tz).formatToParts(new Date(epoch * 1000))
  const n = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? '0')
  return Date.UTC(n('year'), n('month') - 1, n('day'), n('hour'), n('minute'), n('second')) / 1000
}

/** The inverse: the epoch at which `tz` reads this wall-clock time. Two passes
 *  because the offset to subtract is the offset AT the answer, not at the
 *  guess — one refinement settles every zone except the hour a fall-back DST
 *  transition repeats, where either instant is a correct day boundary. */
function epochOfWall(wall: number, tz?: string | null): number {
  const once = wall - (wallClock(wall, tz) - wall)
  return wall - (wallClock(once, tz) - once)
}

/** Percent [0,100] of an epoch across the lane. */
export function laneX(epoch: number, box: LaneBox): number {
  return Math.max(0, Math.min(100, ((epoch - box.dayStart) / box.daySeconds) * 100))
}

/** Left/width percents for a span, clamped to the lane; a floor keeps a
 *  short spike visible (the CSS min-width backs it up). */
export function laneSpan(from: number, till: number, box: LaneBox): { left: number; width: number } {
  const left = laneX(from, box)
  const width = Math.max(0.35, laneX(till, box) - left)
  return { left, width: Math.min(width, Math.max(0.35, 100 - left)) }
}

/** The elevated span the window saw, in epochs — null on clean/no-data days.
 *  Missing cleared_at = still high at the window's close; missing spike_at
 *  (trends only) = the whole window. */
export function windowMark(day: FindingsDay): { from: number; till: number } | null {
  if (day.outcome !== 'spike') return null
  const from = day.spike_at ?? day.start
  const till = day.cleared_at ?? day.end
  return { from, till: Math.max(till, from + 60) }
}

/** "22 min" — or "3h 12m" past ninety minutes (an outage, not a spike) —
 *  when both ends of the in-window mark are known, else null. */
export function laneDuration(day: FindingsDay): string | null {
  if (day.outcome !== 'spike' || day.spike_at == null || day.cleared_at == null) return null
  const min = Math.max(1, Math.round((day.cleared_at - day.spike_at) / 60))
  if (min < 90) return `${min} min`
  const h = Math.floor(min / 60), m = min % 60
  return m ? `${h}h ${m}m` : `${h}h`
}

/** The producer's tally, or one derived from the days when an early v2
 *  block lacks it — the two can never disagree because both count the
 *  same day list. */
export function laneTally(pattern: FindingsPattern): FindingsPatternTally {
  const t = pattern.tally
  if (t && [t.in_window, t.other, t.clean, t.no_data].every((n) => typeof n === 'number')) return t
  return {
    in_window: pattern.days.filter((d) => d.outcome === 'spike').length,
    other: pattern.days.reduce((n, d) => n + daySpans(d).length, 0),
    clean: pattern.days.filter((d) => d.outcome === 'clean').length,
    no_data: pattern.days.filter((d) => d.outcome === 'no_data').length,
  }
}

/** One day's off-window spans, malformed entries dropped (never the lane). */
export function daySpans(day: FindingsDay): FindingsOtherSpan[] {
  if (!Array.isArray(day.other)) return []
  return day.other.filter((s) => !!s && Number.isFinite(s.from) && Number.isFinite(s.till) && s.till > s.from)
}

/** "~03:00" in the VIEWER's clock — the lane the mark sits on is drawn in
 *  that clock, so the label must be too (the server's `label` names the
 *  display zone and can disagree; the first live render put a 12:00 mark
 *  under a "~09:00" label). The server label is only the fallback for an
 *  unreadable epoch. Hour precision keeps its tilde. */
export function spanLabel(span: FindingsOtherSpan, tz?: string | null): string {
  if (!Number.isFinite(span.from)) return span.label ?? ''
  const clock = clockLabel(span.from, tz)
  return span.precision === 'hour' ? `~${clock}` : clock
}

// --- lane time details -------------------------------------------------------
// Beta feedback (2026-09-01): "don't make clients guess when the highlighted
// spikes happened." The scan layer stays terse — captions, the window tag —
// and every mark carries its exact times in a tooltip and in the lane's
// aria-label. All clocks are the VIEWER's (epochs win — the two-clocks rule).

/** `15:58–16:20`, or `~03:00–05:00` when the span is hour-precise. */
export function clockRange(from: number, till: number, approx = false,
                           tz?: string | null): string {
  return `${approx ? '~' : ''}${clockLabel(from, tz)}–${clockLabel(till, tz)}`
}

/** The window column's from–till: `22:23–23:23` · viewer clock. A derived
 *  (recurring / latest-outage) window keeps its tilde — the producer chose
 *  it, the user did not ask for it. The bare start alone left the column's
 *  extent a guess. */
export function windowRange(today: Pick<FindingsDay, 'start' | 'end'>, derived = false,
                            tz?: string | null): string {
  return clockRange(today.start, today.end, derived, tz)
}

/** The solid mark's precise story, one line: when it went over, when it
 *  cleared (or that it hadn't by the window's close), how long, its peak,
 *  the rider. "Already high" days say so honestly — the metric was over the
 *  line before the window opened, so the open is the first thing we saw,
 *  not the start. Null for days without a mark. */
export function markTip(day: FindingsDay, unit: string, word = 'spike',
                        tz?: string | null): string | null {
  if (day.outcome !== 'spike') return null
  const peak = day.peak != null ? ` · peak ${day.peak}${unit}` : ''
  const note = day.note ? ` · ${day.note}` : ''
  const ongoing = word === 'spike' ? 'high' : word
  const from = day.spike_at
  const till = day.cleared_at
  let when: string
  if (from == null) when = `in the window ${clockRange(day.start, day.end, false, tz)}`
  else if (day.spike_at_precision === 'hour') when = `${word} ~${clockLabel(from, tz)}`
  else if (day.caption === 'already high') {
    // spike_at is the first sample seen over the line — NOT the window open
    // (a gap or an in-window rise can put it well past the open; a live run
    // printed "at window open 23:27" under a 23:00 window). Say what the
    // data says: when it was first seen over, and how it ended.
    when = till != null
      ? `already high · first seen over ${clockLabel(from, tz)} · cleared ${clockLabel(till, tz)} · ${laneDuration(day)}`
      : `already high · first seen over ${clockLabel(from, tz)} · still high at window close ${clockLabel(day.end, tz)}`
  } else if (till != null) when = `${word} ${clockRange(from, till, false, tz)} · ${laneDuration(day)}`
  else when = `${word} ${clockLabel(from, tz)} · still ${ongoing} at window close ${clockLabel(day.end, tz)}`
  return `${when}${peak}${note}`
}

/** A hollow (off-window) span's story: `~03:00–05:00 at another time ·
 *  peak 88% · +1 host`. */
export function spanTip(span: FindingsOtherSpan, unit: string, tz?: string | null): string {
  const range = Number.isFinite(span.from) && Number.isFinite(span.till)
    ? clockRange(span.from, span.till, span.precision === 'hour', tz)
    : spanLabel(span, tz)
  const peak = span.peak != null ? ` · peak ${span.peak}${unit}` : ''
  const note = span.note ? ` · ${span.note}` : ''
  const what = span.relative ? 'above this host\'s usual' : 'at another time'
  return `${range} ${what}${peak}${note}`
}

/** The visible clocks for a caption-overridden day — "already high" alone
 *  answered none of when/how long. `22:23–22:32` when both ends are known,
 *  `from 22:23` with only the first-seen-over sample (never "at": that clock
 *  is the first sample over the line, not the window open), `~22:00` on
 *  trend-only days. Null when the derived caption already carries its
 *  clock. */
export function captionRange(day: FindingsDay, tz?: string | null): string | null {
  if (day.outcome !== 'spike' || !day.caption || day.spike_at == null) return null
  if (day.spike_at_precision === 'hour') return `~${clockLabel(day.spike_at, tz)}`
  return day.cleared_at != null
    ? clockRange(day.spike_at, day.cleared_at, false, tz)
    : `from ${clockLabel(day.spike_at, tz)}`
}

/** A ref-only fence resolved from the turn's early-card events: while the
 *  answer is still streaming the fence carries only `{ref}`, and the
 *  `findings_card` event may already hold the block it names. A hydrated
 *  fence (it has a verdict) always wins — the answer text is authoritative. */
export function resolveFindingsRef(
  data: FindingsBlock | null,
  cards: Record<string, FindingsBlock> | undefined,
): FindingsBlock | null {
  if (!data || !cards) return data
  if (data.verdict && typeof data.verdict === 'object') return data
  if (typeof data.ref !== 'string') return data
  const early = cards[data.ref]
  return early && typeof early === 'object' ? early : data
}

// --- heat grid (pattern v2, chronic / plateau) ------------------------------------
// The hourly peaks the classifier already counted, drawn instead of asserted:
// 24 cells per viewer-local day, tinted by how far over the line the hour's
// peak sat. One hue, three steps — magnitude, never identity.

export interface HeatCell { t: number; v: number | null; level: 0 | 1 | 2 | 3 }
export interface HeatRow { day: FindingsDay; cells: HeatCell[]; over: number; withData: number; peak: number | null }
export interface HeatRamp { line: number | null; steps: [number, number, number]; unit: string }

/** The three tint steps above the line, from the data's own headroom: the
 *  range between the line and the week's highest hourly peak, in thirds.
 *  A block without a threshold treats its lowest hour as the line. */
export function heatRamp(pattern: FindingsPattern): HeatRamp {
  const vs = (pattern.hours ?? []).filter(isHourPair).map((h) => h[1])
  const max = vs.length ? Math.max(...vs) : 0
  const min = vs.length ? Math.min(...vs) : 0
  const line = pattern.threshold ?? min
  const span = Math.max(max - line, 0)
  const step = span > 0 ? span / 3 : 1
  return { line: pattern.threshold, steps: [line, line + step, line + 2 * step], unit: pattern.unit }
}

export function heatLevel(v: number, ramp: HeatRamp): 0 | 1 | 2 | 3 {
  if (v < ramp.steps[0]) return 0
  if (v < ramp.steps[1]) return 1
  if (v < ramp.steps[2]) return 2
  return 3
}

/** One row per strip day: 24 hourly cells of that calendar day in the CARD'S
 *  clock (a DST day's extra hour is dropped, its missing hour stays empty),
 *  each hour's peak levelled against the ramp. Hours with no trend row are
 *  null. */
export function heatGrid(pattern: FindingsPattern, ramp: HeatRamp = heatRamp(pattern)): HeatRow[] {
  const hours = (pattern.hours ?? []).filter(isHourPair)
  return pattern.days.map((day) => {
    const box = laneBox(day, pattern.tz)
    const cells: HeatCell[] = Array.from({ length: 24 }, (_, i) => ({ t: box.dayStart + i * 3600, v: null, level: 0 }))
    for (const [t, v] of hours) {
      if (t < box.dayStart || t >= box.dayStart + box.daySeconds) continue
      const i = Math.floor((t - box.dayStart) / 3600)
      if (i < 0 || i > 23) continue
      // Two rows can land in one cell on the 25h DST day — keep the higher peak.
      if (cells[i].v == null || v > cells[i].v!) cells[i] = { t, v, level: heatLevel(v, ramp) }
    }
    const withData = cells.filter((c) => c.v != null)
    const over = withData.filter((c) => c.level > 0).length
    const peak = withData.length ? Math.max(...withData.map((c) => c.v as number)) : null
    return { day, cells, over, withData: withData.length, peak }
  })
}

/** "high all day" / "high all 15 h" / "9 of 24 h" — the row's right-hand read. */
export function heatCaption(row: HeatRow): string {
  if (!row.withData) return 'no data'
  if (row.over === row.withData) return row.withData === 24 ? 'high all day' : `high all ${row.withData} h`
  return `${row.over} of ${row.withData} h`
}

export interface HeatRange { min: number; max: number; over: number; total: number; under: number }

/** The week's hourly peaks against the line: lowest, highest, hours over. */
export function heatRange(pattern: FindingsPattern, ramp: HeatRamp = heatRamp(pattern)): HeatRange | null {
  const vs = (pattern.hours ?? []).filter(isHourPair).map((h) => h[1])
  if (!vs.length) return null
  const over = vs.filter((v) => heatLevel(v, ramp) > 0).length
  return { min: Math.min(...vs), max: Math.max(...vs), over, total: vs.length, under: vs.length - over }
}

/** Compact number for ramp / range labels: 93.4 → "93.4", 90 → "90". */
export function heatNum(v: number): string {
  return Number.isInteger(v) ? String(v) : v.toFixed(1)
}

/** The early cards the streamed text does not reference yet: while the model
 *  is still writing (or drawing its own table instead), the producer's card
 *  already arrived as a `findings_card` event — paint it at the top of the
 *  bubble instead of leaving dead air until the `final` frame. A card whose
 *  ref appears anywhere in the text is NOT orphaned (its fence renders it,
 *  hydrated or resolved from this same map), so nothing draws twice. Order
 *  is arrival order (insertion order of the map). */
export function orphanFindingsCards(
  content: string,
  cards: Record<string, FindingsBlock> | undefined,
): Array<{ ref: string; block: FindingsBlock }> {
  if (!cards) return []
  const out: Array<{ ref: string; block: FindingsBlock }> = []
  for (const [ref, block] of Object.entries(cards)) {
    if (!block || typeof block !== 'object') continue
    if (content && content.includes(ref)) continue
    out.push({ ref, block })
  }
  return out
}

// --- stream-time duplicate suppression --------------------------------------
// Mirrors app/graphs/primitives/findings_blocks.py: the server drops a model
// table that restates a producer card's rows, but only on the terminal frame —
// so mid-stream the duplicate used to build for seconds and then vanish when
// `final` landed. The client holds the same card blocks the server placed, so
// it can run the same test on the streaming text and never show a table the
// final frame is known to remove. Same thresholds and figure filter as the
// server; the haystack is every string the cards carry (the client's analogue
// of the producer tool results the server compares against).

const DUP_MIN_FRACTION = 0.5
const DUP_MIN_MATCHES = 2
const DUP_MIN_CHARS = 4
// A cell that is just a figure ("89% used", "13 GB", "High") identifies
// nothing and matches everything — names are the signal, so only they count.
const FIGURE_RE = /^[\d\s.,%×xGMKTiBmshd\/+-]*[a-zA-Z]{0,4}$/

/** A cell worth comparing: markdown dressing stripped, long enough to name a
 *  subject, carrying letters, and not figure-shaped. Null otherwise. */
function identifyingCell(raw: string): string | null {
  const text = raw.replace(/[`*_]/g, '').trim()
  if (text.length < DUP_MIN_CHARS) return null
  if (!/\p{L}/u.test(text)) return null
  if (FIGURE_RE.test(text)) return null
  return text
}

/** Every string the cards carry, joined — the substring haystack. */
function cardStrings(cards: Record<string, FindingsBlock>): string {
  const out: string[] = []
  const walk = (node: unknown): void => {
    if (typeof node === 'string') out.push(node)
    else if (Array.isArray(node)) node.forEach(walk)
    else if (node && typeof node === 'object') Object.values(node as Record<string, unknown>).forEach(walk)
  }
  walk(cards)
  return out.join('\n')
}

// A markdown table, as the server matches it — plus, for streaming, an
// optional trailing data-row fragment at the very end of the content, so a
// dropped table's half-written last row does not linger as stray text.
const MD_TABLE_RE =
  /(?:^|\n)[ \t]{0,3}\|[^\n]*\|[ \t]*\n[ \t]{0,3}\|[ \t:|-]+\|[ \t]*\n(?:[ \t]{0,3}\|[^\n]*\|[ \t]*(?:\n|$))+(?:[ \t]{0,3}\|[^\n]*$)?/g

/** Identifying cells of a markdown table's complete data rows (header and
 *  separator are column words, not subjects; an unfinished row waits). */
function mdTableCells(table: string): Set<string> {
  const lines = table.trim().split('\n').filter((ln) => ln.trimStart().startsWith('|'))
  const out = new Set<string>()
  for (const ln of lines.slice(2)) {
    const row = ln.trim()
    if (!row.endsWith('|')) continue
    for (const cell of row.replace(/^\|+|\|+$/g, '').split('|')) {
      const text = identifyingCell(cell)
      if (text !== null) out.add(text)
    }
  }
  return out
}

// A complete ```netwatch-table fence (an unclosed one renders as "Building…",
// never as a table, so only closed fences need the test).
const TABLE_FENCE_RE = /```[ \t]*netwatch-table[^\n]*\n[\s\S]*?\n?```/g

/** Identifying cells of a table fence's rows; an unparseable fence yields
 *  nothing — never drop what can't be read. */
function fenceCells(fence: string): Set<string> {
  const body = fence.replace(/^```[^\n]*\n?/, '').replace(/\n?```$/, '')
  let data: unknown
  try {
    data = JSON.parse(body)
  } catch {
    return new Set()
  }
  const out = new Set<string>()
  const walk = (node: unknown): void => {
    if (typeof node === 'string') {
      const text = identifyingCell(node)
      if (text !== null) out.add(text)
    } else if (Array.isArray(node)) node.forEach(walk)
    else if (node && typeof node === 'object') Object.values(node as Record<string, unknown>).forEach(walk)
  }
  walk((data as { rows?: unknown } | null)?.rows)
  return out
}

/** The server's bar, verbatim: two-plus identifying cells and at least half
 *  of them found in the haystack. */
function restatesCards(cells: Set<string>, haystack: string): boolean {
  if (!cells.size) return false
  let matched = 0
  for (const c of cells) if (haystack.includes(c)) matched++
  return matched >= DUP_MIN_MATCHES && matched >= DUP_MIN_FRACTION * cells.size
}

/** Remove, from a still-streaming answer, any table (fenced or markdown)
 *  whose rows restate a card this turn already delivered — the same tables
 *  `ensure_producer_cards` removes from the final frame, taken out before
 *  they ever paint. Run this only while the turn is busy: the final text is
 *  authoritative and is never rewritten. */
export function dropDuplicateTables(
  content: string,
  cards: Record<string, FindingsBlock> | undefined,
): string {
  if (!content || !cards) return content
  if (!content.includes('|') && !content.includes('```')) return content
  const haystack = cardStrings(cards)
  if (!haystack) return content
  return content
    .replace(TABLE_FENCE_RE, (m) => (restatesCards(fenceCells(m), haystack) ? '\n' : m))
    .replace(MD_TABLE_RE, (m) => (restatesCards(mdTableCells(m), haystack) ? '\n' : m))
}

export function formatCorrelation(r: number | null | undefined): string {
  if (typeof r !== 'number' || Number.isNaN(r)) return '—'
  return `${r >= 0 ? '+' : '−'}${Math.abs(r).toFixed(2)}`
}

// --- shaped tables (docs/findings-everywhere.md, "Shaped tables") ------------
// Pure decisions the component draws from; the grid is always the fallback.

export type TableShapeKind = 'grid' | FindingsTableShape

/** The column the bars shape draws as a bar: the first non-lead column any
 *  row carries a fill for. */
export function barColumn(table: FindingsTable): string | null {
  for (const c of table.columns.slice(1)) {
    if (table.rows.some((r) => r.fills && typeof r.fills[c.key] === 'number')) return c.key
  }
  return null
}

/** Which visual a table earns. A hint is honoured only when its data is
 *  there: bars need a filled column, score needs its readout; tiles need
 *  nothing beyond a lead column (chips and pairs carry a fill-less row). */
export function tableShape(table: FindingsTable): TableShapeKind {
  if (!table.rows.length || !table.columns.length) return 'grid'
  if (table.shape === 'bars') return table.columns.length >= 2 && barColumn(table) ? 'bars' : 'grid'
  if (table.shape === 'score') return table.score ? 'score' : 'grid'
  if (table.shape === 'tiles') return 'tiles'
  return 'grid'
}

/** A fill as a percent width, clamped. */
export function fillPct(f: number | undefined | null): number {
  if (typeof f !== 'number' || !Number.isFinite(f)) return 0
  return Math.max(0, Math.min(100, Math.round(f * 1000) / 10))
}

/** The meter's colour: the row's own tone when it has one, else amber once
 *  the fill crosses the floor, else the quiet fill. */
export function meterTone(fill: number, floor: number | null | undefined, rowTone?: FindingsRowTone): 'strong' | 'warn' | 'none' {
  if (rowTone === 'strong' || rowTone === 'warn') return rowTone
  if (typeof floor === 'number' && fill >= floor) return 'warn'
  return 'none'
}

/** One label/value pair on a tile: the column, its cell, and its fill. The
 *  lead column is the tile's name; a pair whose words the chip or sub
 *  already say is dropped (a "state" cell under a "connected" chip), and so
 *  is an empty cell with nothing to draw. */
export interface TilePair { key: string; label: string; value: string; fill: number | null }
export function tilePairs(table: FindingsTable, row: FindingsTableRow): TilePair[] {
  const out: TilePair[] = []
  for (const c of table.columns.slice(1)) {
    const value = typeof row[c.key] === 'string' ? (row[c.key] as string) : ''
    const fill = row.fills && typeof row.fills[c.key] === 'number' ? row.fills[c.key] : null
    if (fill === null) {
      if (!value || value === '—') continue
      if (row.chip && (row.chip.text === value || row.chip.text.startsWith(value))) continue
      if (row.sub && row.sub.includes(value)) continue
    }
    out.push({ key: c.key, label: c.label, value, fill })
  }
  return out
}

/** The runway axis: now · 1 week · 1 month · 3 months, then "stable" at the
 *  end for drives that are not filling. Piecewise linear between the stops
 *  so a week reads as a week, not as 7/90 of the strip. */
export const RUNWAY_STOPS: Array<{ days: number; x: number; label: string }> = [
  { days: 0, x: 0, label: 'now' },
  { days: 7, x: 26, label: '1 week' },
  { days: 30, x: 52, label: '1 month' },
  { days: 90, x: 78, label: '3 months' },
]
export const RUNWAY_STABLE_X = 100

export function runwayX(days: number | null): number {
  if (days === null || !Number.isFinite(days)) return RUNWAY_STABLE_X
  if (days <= 0) return 0
  for (let i = 1; i < RUNWAY_STOPS.length; i++) {
    const a = RUNWAY_STOPS[i - 1], b = RUNWAY_STOPS[i]
    if (days <= b.days) return a.x + ((days - a.days) / (b.days - a.days)) * (b.x - a.x)
  }
  // Past the last stop: creep toward, never onto, the stable mark.
  const last = RUNWAY_STOPS[RUNWAY_STOPS.length - 1]
  return Math.min(92, last.x + ((days - last.days) / last.days) * 8)
}

/** "~6 d" / "<1 d" beside a runway dot; null for a parked (stable) one. */
export function runwayDays(days: number | null): string | null {
  if (days === null || !Number.isFinite(days)) return null
  return days < 1 ? '<1 d' : `~${Math.round(days)} d`
}

/** Runway dots laid out: filling drives sorted by days with alternating
 *  label rows when two land within `gap` percent, stable ones gathered
 *  into one caption at the end. */
export interface RunwayLayout {
  dots: Array<FindingsRunwayDot & { x: number; row: 0 | 1; text: string }>
  stable: string[]
}
export function runwayLayout(dots: FindingsRunwayDot[], gap = 9): RunwayLayout {
  const filling = dots.filter((d) => d.days !== null).sort((a, b) => (a.days ?? 0) - (b.days ?? 0))
  const placed: RunwayLayout['dots'] = []
  let lastX = -100, lastRow: 0 | 1 = 1
  for (const d of filling) {
    const x = runwayX(d.days)
    const row: 0 | 1 = x - lastX < gap ? (lastRow === 0 ? 1 : 0) : 0
    placed.push({ ...d, x, row, text: `${d.label} · ${runwayDays(d.days)}` })
    lastX = x; lastRow = row
  }
  return { dots: placed, stable: dots.filter((d) => d.days === null).map((d) => d.label) }
}

/** Where the score sits on its track, and which band it is in. */
export function scoreX(score: FindingsScore): number {
  return Math.max(0, Math.min(100, (score.value / score.max) * 100))
}
export function scoreLevel(score: FindingsScore): 'strong' | 'warn' | 'ok' {
  const [low, high] = [score.bands[0], score.bands[1] ?? score.bands[0]]
  if (typeof low === 'number' && score.value < low) return 'strong'
  if (typeof high === 'number' && score.value < high) return 'warn'
  return 'ok'
}

/** The tally row's words: the producer's, else the metric-card defaults. */
export function tallyWords(pattern: FindingsPattern): Required<NonNullable<FindingsPattern['tally_labels']>> {
  const t = pattern.tally_labels ?? {}
  return {
    in_window: t.in_window || 'in this window',
    other: t.other || 'at other times',
    clean: t.clean || 'clean',
    no_data: t.no_data || 'without data',
  }
}
